import type { SoundName, AudioManifest } from '../types';
import { getCachedSound, setCachedSound, isCacheAvailable } from './audioCache';

/**
 * Determine if a sound should be played as a continuous looping sound
 * vs a discrete beep that triggers at extremes
 */
function isContinuousSound(soundName: SoundName): boolean {
  // Continuous looping sounds with smooth panning
  const continuousSounds: SoundName[] = ['ocean', 'ambient', 'angelic', 'handpan', 'underwater', 'drone'];
  return continuousSounds.includes(soundName);
}

/**
 * BLSAudioEngine - Manages bilateral audio stimulation using Web Audio API
 *
 * Features:
 * - Preloads all audio files on initialization
 * - Plays sounds with stereo panning (left/right alternation)
 * - Configurable volume and speed
 * - Low-latency playback using AudioContext
 */
export class BLSAudioEngine {
  private audioContext: AudioContext;
  private sounds: Map<SoundName, AudioBuffer> = new Map();
  private currentVolume: number = 0.7;
  private isLoaded: boolean = false;
  private loadingPromise: Promise<void> | null = null;
  private activeSources: AudioBufferSourceNode[] = []; // Track active audio sources

  // Continuous sound support (for looping sounds with panning)
  // Map allows multiple continuous sounds to play simultaneously
  private continuousSources: Map<SoundName, {
    source: AudioBufferSourceNode;
    panner: StereoPannerNode;
    gain: GainNode;
  }> = new Map();

  // Bilateral audio synchronization (position-based beep triggering)
  // Map tracks last beep side for each sound separately (allows multiple beeps simultaneously)
  private lastBeepSide: Map<SoundName, number> = new Map();

  // Audio manifest cache (for cache validation)
  private manifestCache: AudioManifest | null = null;

  // Mixed mode support
  private mixedModeSounds: SoundName[] = [];
  private isMixedMode: boolean = false;

  constructor() {
    // Create AudioContext (Safari requires user interaction to unlock)
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }

  /**
   * Load a single sound file (with caching)
   */
  private async loadSound(name: SoundName, url: string): Promise<void> {
    try {
      // Check if caching is available
      const cacheAvailable = isCacheAvailable();

      // Step 1: Load manifest (once, cached in memory)
      const manifest = await this.getManifest();
      const expectedHash = manifest[name]?.hash;

      if (!expectedHash) {
        console.warn(`No manifest entry for ${name}, loading without cache`);
        await this.loadSoundWithoutCache(name, url);
        return;
      }

      // Step 2: Check cache (if available)
      if (cacheAvailable) {
        const cached = await getCachedSound(name);

        // Step 3: If cache hit and hash matches, use cache
        if (cached && cached.fileHash === expectedHash) {
          console.log(`✓ Cache hit for ${name} (hash matched)`);
          const audioBuffer = await this.audioContext.decodeAudioData(cached.arrayBuffer);
          this.sounds.set(name, audioBuffer);
          return;
        }

        if (cached) {
          console.log(`⚠ Cache stale for ${name} (hash mismatch)`);
        }
      }

      // Step 4: Cache miss or stale - download from network
      console.log(cacheAvailable ? `⊗ Cache miss for ${name}, downloading...` : `Fetching ${name} from ${url}`);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      // Step 5: Store in cache (if available)
      if (cacheAvailable) {
        await setCachedSound(name, {
          soundName: name,
          arrayBuffer,
          fileHash: expectedHash,
          cachedAt: Date.now(),
          fileSize: arrayBuffer.byteLength,
          url,
        });
      }

      // Step 6: Decode and use
      console.log(`Decoding ${name}, size: ${arrayBuffer.byteLength} bytes`);
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.sounds.set(name, audioBuffer);
      console.log(`Successfully loaded: ${name}`);

    } catch (error) {
      const errorMsg = `Failed to load ${name} from ${url}: ${error instanceof Error ? error.message : error}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * Load sound without caching (fallback)
   */
  private async loadSoundWithoutCache(name: SoundName, url: string): Promise<void> {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    console.log(`Decoding ${name}, size: ${arrayBuffer.byteLength} bytes`);
    const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    this.sounds.set(name, audioBuffer);
    console.log(`Successfully loaded: ${name}`);
  }

  /**
   * Get audio manifest (cached in memory after first load)
   */
  private async getManifest(): Promise<AudioManifest> {
    if (this.manifestCache) {
      return this.manifestCache;
    }

    try {
      const response = await fetch('/audio-manifest.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch manifest: ${response.status}`);
      }
      this.manifestCache = await response.json();
      console.log('✓ Loaded audio manifest');
      return this.manifestCache!;
    } catch (error) {
      console.error('Failed to load manifest, caching disabled:', error);
      // Return empty manifest to disable caching
      return {} as AudioManifest;
    }
  }

  /**
   * Map of all sound files
   */
  private readonly soundFiles: Array<{ name: SoundName; url: string }> = [
    // Discrete beep sounds
    { name: 'beep', url: '/assets/sounds/beep.mp3' },
    { name: 'tap', url: '/assets/sounds/tap.mp3' },
    { name: 'heartbeat', url: '/assets/sounds/heartbeat.mp3' },
    { name: 'rain', url: '/assets/sounds/rain.mp3' },
    { name: 'snap', url: '/assets/sounds/snap.mp3' },
    { name: 'wood', url: '/assets/sounds/wood.mp3' },
    { name: 'bamboo', url: '/assets/sounds/bamboo.mp3' },
    { name: 'drum', url: '/assets/sounds/drum.mp3' },
    { name: 'tibetan_ping', url: '/assets/sounds/tibetan_ping.mp3' },

    // Continuous looping sounds
    { name: 'ocean', url: '/assets/sounds/ocean.mp3' },
    { name: 'ambient', url: '/assets/sounds/ambient.mp3' },
    { name: 'angelic', url: '/assets/sounds/angelic.mp3' },
    { name: 'handpan', url: '/assets/sounds/handpan.mp3' },
    { name: 'underwater', url: '/assets/sounds/underwater.mp3' },
    { name: 'drone', url: '/assets/sounds/drone.mp3' },
  ];

  /**
   * Load specific sounds (priority loading for immediate playback)
   * @param soundNames - Array of sound names to load immediately
   * @returns Promise that resolves when requested sounds are loaded
   */
  async loadSounds(soundNames: SoundName[]): Promise<void> {
    const filesToLoad = this.soundFiles.filter(f => soundNames.includes(f.name));

    console.log(`Priority loading ${filesToLoad.length} sounds:`, soundNames);

    // Load in parallel
    const results = await Promise.allSettled(
      filesToLoad.map(({ name, url }) => this.loadSound(name, url))
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;

    if (succeeded > 0) {
      this.isLoaded = true;
      console.log(`Loaded ${succeeded}/${filesToLoad.length} priority sounds`);
    } else {
      throw new Error('Failed to load any requested sounds');
    }
  }

  /**
   * Load remaining sounds in background (non-blocking)
   * Loads all sounds that haven't been loaded yet
   */
  loadRemainingInBackground(): void {
    const remaining = this.soundFiles.filter(f => !this.sounds.has(f.name));

    if (remaining.length === 0) {
      console.log('All sounds already loaded');
      return;
    }

    console.log(`Background loading ${remaining.length} remaining sounds...`);

    // Load in parallel, don't await (non-blocking)
    Promise.allSettled(
      remaining.map(({ name, url }) => this.loadSound(name, url))
    ).then((results) => {
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      console.log(`Background load complete: ${succeeded}/${remaining.length} sounds`);
    });
  }

  /**
   * Load all sound files
   */
  async loadAllSounds(): Promise<void> {
    if (this.isLoaded) return;
    if (this.loadingPromise) return this.loadingPromise;

    // Load all files in parallel
    this.loadingPromise = Promise.allSettled(
      this.soundFiles.map(({ name, url }) => this.loadSound(name, url))
    ).then((results) => {
      const failed: string[] = [];
      const succeeded: string[] = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          succeeded.push(this.soundFiles[index].name);
        } else {
          failed.push(this.soundFiles[index].name);
        }
      });

      console.log(`Loaded ${succeeded.length}/${this.soundFiles.length} audio files`);
      if (failed.length > 0) {
        console.warn(`Failed to load: ${failed.join(', ')}`);
      }

      // Consider loaded if we have at least some sounds
      if (succeeded.length >= 2) {
        this.isLoaded = true;
        console.log('Audio engine ready with available sounds');
      } else {
        throw new Error(`Only ${succeeded.length} sounds loaded. Need at least 2.`);
      }
    });

    return this.loadingPromise;
  }

  /**
   * Resume AudioContext (required after user interaction in some browsers)
   */
  async resumeContext(): Promise<void> {
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  /**
   * Check if a sound should be played as continuous (looping with panning)
   */
  isContinuousSound(soundName: SoundName): boolean {
    return isContinuousSound(soundName);
  }

  /**
   * Play a sound with stereo panning
   * @param soundName - Name of the sound to play
   * @param pan - Stereo position: -1 (left), 0 (center), 1 (right)
   */
  playSound(soundName: SoundName, pan: number = 0): void {
    const buffer = this.sounds.get(soundName);
    if (!buffer) {
      console.warn(`Sound not loaded: ${soundName}`);
      return;
    }

    try {
      // Create audio nodes
      const source = this.audioContext.createBufferSource();
      const panner = this.audioContext.createStereoPanner();
      const gainNode = this.audioContext.createGain();

      // Configure nodes
      source.buffer = buffer;
      panner.pan.value = Math.max(-1, Math.min(1, pan)); // Clamp between -1 and 1
      gainNode.gain.value = this.currentVolume;

      // Connect: source -> panner -> gain -> destination
      source.connect(panner);
      panner.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Track active source
      this.activeSources.push(source);

      // Clean up when sound finishes
      source.onended = () => {
        const index = this.activeSources.indexOf(source);
        if (index > -1) {
          this.activeSources.splice(index, 1);
        }
      };

      // Play
      source.start(0);
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }

  /**
   * Set the master volume
   * @param volume - Volume level (0-1)
   */
  setVolume(volume: number): void {
    this.currentVolume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Get the current volume
   */
  getVolume(): number {
    return this.currentVolume;
  }

  /**
   * Update bilateral audio panning based on visual position
   * For discrete beep sounds: Triggers beeps at extremes (when dot reaches left/right edges)
   * For continuous sounds: Updates smooth panning only
   * @param panValue - Current pan value from -1 (left) to 1 (right)
   * @param soundName - Name of the sound to play
   */
  updateBilateralPan(panValue: number, soundName: SoundName): void {
    // Check if this is a continuous looping sound (rain, ocean)
    if (isContinuousSound(soundName)) {
      // For continuous sounds, only update panning (smooth pan following dot)
      this.updateContinuousPan(panValue);
      return;
    }

    // For discrete beep sounds, trigger beeps at position extremes
    const EXTREME_THRESHOLD = 0.85; // Trigger beep when within 15% of edge

    // Get last beep side for this specific sound (default to 0 if not tracked yet)
    const lastSide = this.lastBeepSide.get(soundName) || 0;

    // Detect right extreme: moving right and reached threshold
    if (panValue > EXTREME_THRESHOLD && lastSide !== 1) {
      // In mixed mode, pick random sound from available list
      const soundToPlay = this.isMixedMode && this.mixedModeSounds.length > 0
        ? this.mixedModeSounds[Math.floor(Math.random() * this.mixedModeSounds.length)]
        : soundName;
      this.playSound(soundToPlay, 1); // RIGHT ear
      this.lastBeepSide.set(soundName, 1);
    }

    // Detect left extreme: moving left and reached threshold
    else if (panValue < -EXTREME_THRESHOLD && lastSide !== -1) {
      // In mixed mode, pick random sound from available list
      const soundToPlay = this.isMixedMode && this.mixedModeSounds.length > 0
        ? this.mixedModeSounds[Math.floor(Math.random() * this.mixedModeSounds.length)]
        : soundName;
      this.playSound(soundToPlay, -1); // LEFT ear
      this.lastBeepSide.set(soundName, -1);
    }

    // Reset beep side when passing through center (allows next beep to trigger)
    if (Math.abs(panValue) < 0.3) {
      this.lastBeepSide.set(soundName, 0);
    }
  }

  /**
   * Start continuous sound (loops with panning)
   * Used for sounds like ocean, ambient, etc. that should play continuously
   * @param soundName - Name of the sound to play
   * @param volume - Volume level (0-1)
   */
  async startContinuous(soundName: SoundName, volume: number = 0.7): Promise<void> {
    const buffer = this.sounds.get(soundName);
    if (!buffer) {
      console.warn(`Sound not loaded: ${soundName}`);
      return;
    }

    // Stop this specific sound if already playing
    this.stopContinuousSound(soundName);

    try {
      // Resume context if suspended
      await this.resumeContext();

      // Create nodes for this sound
      const source = this.audioContext.createBufferSource();
      const panner = this.audioContext.createStereoPanner();
      const gain = this.audioContext.createGain();

      // Configure
      source.buffer = buffer;
      source.loop = true; // KEY: Loop the audio
      panner.pan.value = 0; // Start centered
      gain.gain.value = volume;

      // Connect chain: source -> panner -> gain -> destination
      source.connect(panner).connect(gain).connect(this.audioContext.destination);

      // Start playing (will loop forever)
      source.start(0);

      // Store in map
      this.continuousSources.set(soundName, { source, panner, gain });

      console.log(`Started continuous sound: ${soundName}`);
    } catch (error) {
      console.error('Error starting continuous sound:', error);
    }
  }

  /**
   * Stop a specific continuous sound
   * @param soundName - Name of the sound to stop
   */
  stopContinuousSound(soundName: SoundName): void {
    const nodes = this.continuousSources.get(soundName);
    if (!nodes) return;

    try {
      nodes.source.stop();
      nodes.source.disconnect();
      nodes.panner.disconnect();
      nodes.gain.disconnect();
    } catch (error) {
      // Already stopped/disconnected, ignore
    }

    this.continuousSources.delete(soundName);
  }

  /**
   * Update panning for all continuous sounds based on visual position
   * @param panValue - Pan value from -1 (left) to 1 (right)
   */
  updateContinuousPan(panValue: number): void {
    const clampedPan = Math.max(-1, Math.min(1, panValue));

    // Update panning for ALL active continuous sounds
    for (const nodes of this.continuousSources.values()) {
      nodes.panner.pan.value = clampedPan;
    }
  }

  /**
   * Stop all continuous sounds
   */
  stopContinuous(): void {
    // Stop all continuous sounds
    for (const soundName of this.continuousSources.keys()) {
      this.stopContinuousSound(soundName);
    }
  }

  /**
   * Stop all sounds (both continuous and discrete beeps)
   */
  stopAll(): void {
    // Stop all active discrete beep sounds immediately
    this.activeSources.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (error) {
        // Ignore if already stopped
      }
    });
    this.activeSources = [];

    // Stop continuous sound
    this.stopContinuous();

    // Reset beep tracking for all sounds
    this.lastBeepSide.clear();
  }

  /**
   * Check if sounds are loaded
   */
  isReady(): boolean {
    return this.isLoaded;
  }

  /**
   * Get AudioContext state
   */
  getContextState(): AudioContextState {
    return this.audioContext.state;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    // Stop all active sources
    this.activeSources.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (error) {
        // Ignore if already stopped
      }
    });
    this.activeSources = [];

    // Stop continuous sound
    this.stopContinuous();

    this.sounds.clear();
    if (this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }

  /**
   * Enable mixed mode sound randomization
   * @param sounds - Array of sounds to randomly choose from
   */
  enableMixedMode(sounds: SoundName[]): void {
    this.isMixedMode = true;
    this.mixedModeSounds = sounds;
    console.log('[AudioEngine] Mixed mode enabled with sounds:', sounds);
  }

  /**
   * Disable mixed mode
   */
  disableMixedMode(): void {
    this.isMixedMode = false;
    this.mixedModeSounds = [];
    console.log('[AudioEngine] Mixed mode disabled');
  }
}

// Create singleton instance
let audioEngineInstance: BLSAudioEngine | null = null;

/**
 * Get the singleton audio engine instance
 */
export function getAudioEngine(): BLSAudioEngine {
  if (!audioEngineInstance) {
    audioEngineInstance = new BLSAudioEngine();
  }
  return audioEngineInstance;
}
